import {
  FoBannerControllerService,
  FoBannerVo,
} from '@/__generated__/FrontApi';
import iconFacebook from '@/assets/img/join/ico_facebook@2x.png';
import iconGoogle from '@/assets/img/join/ico_google@2x.png';
import Button from '@/components/Button';
import CampaignCard, { CampaignData } from '@/components/Card/CampaignCard';
import ErrorMessage from '@/components/Form/ErrorMessage';
import FormGroup from '@/components/Form/FormGroup';
import Row from '@/components/Grid/Row';
import InputCheckbox from '@/components/Input/InputCheckbox';
import InputPassword from '@/components/Input/InputPassword';
import InputText from '@/components/Input/InputText';
import { Tit } from '@/components/Titles';
import { breakpoint } from '@/helpers/BreakpointHelper';
import LayoutWithoutTitle from '@/layouts/LayoutWithoutTitle';
import {
  FormContainer,
  PageTitle,
  SocialButton,
} from '@/page-blocks/auth/AuthCommon';
import LocalStorage from '@/services/LocalStorage';
import { usePopupStore } from '@/stores/PopupStore';
import { useUserStore } from '@/stores/UserStore';
import { useGoogleLogin } from '@react-oauth/google';
import { Link, navigate, PageProps } from 'gatsby';
import { observer } from 'mobx-react-lite';
import queryString from 'query-string';
import React, { useCallback, useEffect, useState } from 'react';
import FacebookLogin from '@greatsumini/react-facebook-login';
import { SubmitHandler, useForm } from 'react-hook-form';
import styled from 'styled-components';
import KakaoLogin, {
  KakaoSimpleJoin,
  KakaoUser,
} from '@/components/SocialLogin/KakaoLogin';
import NaverLogin, {
  NaverSuccessResult,
} from '@/components/SocialLogin/NaverLogin';
import LoginLoading from '@/components/LoginLoading';

const LoginPageBanners = styled.div`
  padding-top: 50px;

  .bn-slide {
    .swiper-slide {
      width: 100%;

      .img-box {
        padding-top: 19.64%;
      }
    }
  }
`;

interface LoginFormData {
  loginId: string;
  loginPw: string;
  isSavingId: boolean;
}

interface LoginLocationState {
  prevPath?: string;
  items?: any;
}

const Login: React.FC<
  PageProps<any, any, LoginLocationState | null>
> = observer(({ location }) => {
  //const prevPath = location?.state ? location.state?.prevPath ?? `` : '';
  const excludeList = ['/auth/join']; // 메인으로 보낼 URL
  const statePrevPath = location?.state ? location.state?.prevPath ?? `` : '';
  const prevPath = excludeList.some((path) => statePrevPath.startsWith(path))
    ? ''
    : statePrevPath;
  const igItems = location?.state?.items;
  const { redirectTo } = queryString.parse(location.search);
  const hashStr = location?.hash == null ? `` : location?.hash;
  const userStore = useUserStore();
  const popupStore = usePopupStore();
  const [loginLoading, setLoginLoading] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    defaultValues: {
      loginId: LocalStorage.getItem(`savedLoginId`) || ``,
      isSavingId: !!LocalStorage.getItem(`savedLoginId`),
    },
  });
  const navigateNext = useCallback(() => {
    // redirectTo url을 받아와서 로그인후 넘어가기, default는 홈
    navigate(
      (((redirectTo == null ||
      redirectTo === undefined ||
      /^((\w*:)|)\/\//.test(`${redirectTo}`) === true
        ? prevPath
        : redirectTo) + hashStr) as string) || `/`,
      {
        state: {
          // 개인정보수정 등 재인증이 필요한 구간에서 인증 skip하기 위함
          skipAuth: true,
          // IG 장바구니
          items: igItems,
        },
      },
    );
  }, [hashStr, redirectTo, prevPath]);

  /* *************** 네이버 로그인 : s */
  const loginWithNaver = useCallback(
    async (result: NaverSuccessResult) => {
      try {
        setLoginLoading(true);
        const error = await userStore.loginSocial(
          `naver`,
          result.accessToken as string,
        );
        if (error && error.socialId) {
          setLoginLoading(false);
          popupStore.show(error.returnMessage, {
            onConfirm: () => navigate(`/auth/join`),
          });
        }
        navigate('/'); // navigateNext() 실행시 무한루프 오류 있음 수정 필요
      } catch (e) {
        setLoginLoading(false);
        // eslint-disable-next-line no-console
        console.log(e);
      }
    },
    [popupStore, userStore],
  );
  /* *************** 네이버 로그인 : e */

  /* *************** 카카오 로그인 : s */
  const loginWithKakao = useCallback(
    async (kakaoInfo: KakaoUser) => {
      try {
        setLoginLoading(true);
        const error = await userStore.loginSocial(
          `kakao`,
          kakaoInfo.accessToken,
        );
        // 로그인 정보 없음
        if (error && error.socialId) {
          setLoginLoading(false);
          if (error.socialId) {
            // 연락처를 받아오지 못한 경우 안내 후 회원가입 폼으로 이동
            if (!kakaoInfo.convertPhoneNumber) {
              alert(
                '휴대전화 정보가 확인되지 않아 추가 정보 입력이 필요합니다.\n회원가입 페이지로 이동합니다.',
              );
              navigate(`/auth/join/form`, {
                state: { kakao: kakaoInfo },
              });
              return;
            }
            // 간편가입 진행
            const joinResult = await KakaoSimpleJoin(kakaoInfo);
            if (joinResult === null) {
              popupStore.show(
                '간편가입에 실패했습니다. 나중에 다시 시도해주세요.',
              );
            } else {
              if (joinResult.resultCode === 'success') {
                if (joinResult.resultState === 'JOIN') {
                  // 신규가입
                  navigate(`/auth/join/complete`);
                } else if (joinResult.resultState === 'LINKED') {
                  // 계정 연결
                  navigate(`/auth/join/complete`);
                }
              } else {
                popupStore.show(`${joinResult.resultMessage}`);
              }
            }
          } else {
            popupStore.show('인증에 실패했습니다. 나중에 다시 시도해주세요.');
          }
        } else {
          navigate('/');
        }
      } catch (e) {
        setLoginLoading(false);
        console.log(e);
      }
    },
    [popupStore, userStore],
  );
  /* *************** 카카오 로그인 : e */

  // 일반 로그인 (폼 submit시)
  const onSubmit: SubmitHandler<LoginFormData> = async ({
    loginId,
    loginPw,
    isSavingId,
  }) => {
    // 아이디 저장
    if (isSavingId) {
      LocalStorage.setItem(`savedLoginId`, loginId.trim());
    } else {
      LocalStorage.removeItem(`savedLoginId`);
    }

    setLoginLoading(true);
    const error = await userStore.login({ loginId, loginPw });
    if (error) {
      setLoginLoading(false);
      popupStore.show(error);
    }
  };

  // 페이스북 로그인
  const loginWithFacebook = useCallback(
    async ({ accessToken }) => {
      if (!accessToken) {
        return;
      }
      setLoginLoading(true);
      const error = await userStore.loginSocial(`facebook`, accessToken);
      if (error && error.socialId) {
        setLoginLoading(false);
        popupStore.show(error.returnMessage, {
          onConfirm: () => navigate(`/auth/join`),
        });
      }
    },
    [popupStore, userStore],
  );

  // 구글 로그인
  const loginWithGoogle = useGoogleLogin({
    onSuccess: async (res) => {
      setLoginLoading(true);
      const error = await userStore.loginSocial(`google`, res.code);
      if (error && error.socialId) {
        setLoginLoading(false);
        popupStore.show(error.returnMessage, {
          onConfirm: () => navigate(`/auth/join`),
        });
      }
    },
    // eslint-disable-next-line no-console
    onError: (e) => console.error(e),
    flow: 'auth-code',
  });

  const googleLoginClick = () => {
    const ua = window.navigator.userAgent;
    const target_url = window.location.href;
    if (ua.match(/kakaotalk/i)) {
      if (
        confirm(
          `카카오톡에서는 구글 간편 로그인이 어렵습니다.\n확인 버튼을 누르면 로그인을 위해 기본 브라우저로 이동합니다.`,
        )
      ) {
        window.location.href =
          'kakaotalk://web/openExternal?url=' + encodeURIComponent(target_url);
      }
    } else if (ua.match(/naver/i)) {
      if (ua.match(/android/i)) {
        if (
          confirm(
            `NAVER 앱 브라우저에서는 구글 간편 로그인이 지원되지 않습니다.\n다른 수단 또는 외부 브라우저를 이용해 주세요.`,
          )
        ) {
          window.location.href =
            'intent://' +
            target_url.replace(/https?:\/\//i, '') +
            '#Intent;scheme=http;package=com.android.chrome;end';
        }
      } else {
        alert(
          `NAVER 앱 브라우저에서는 구글 간편 로그인이 지원되지 않습니다.\n다른 수단 또는 외부 브라우저를 이용해 주세요.`,
        );
      }
    } else {
      loginWithGoogle();
    }
  };

  useEffect(() => {
    // 로그인 되었으면 이동
    if (userStore.user) {
      navigateNext();
      // navigate(`/`);
    }
  }, [userStore.user]);

  const [banners, setBanners] = useState<CampaignData[]>([]);

  const loadBanners = useCallback(async () => {
    try {
      const {
        data: loginPageBanners,
      } = await FoBannerControllerService.selectBannerListUsingGet({
        kind: '6001',
      });
      setBanners(
        loginPageBanners.map((banner: FoBannerVo) => ({
          id: banner.indexNumber,
          pcImage: banner.imgAttGrpNoPc,
          mobileImage: banner.imgAttGrpNoMobile,
          link: banner.targetUrl,
          linkTargetBlank: banner.targetBlankYn === `Y`,
        })),
      );
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    loadBanners();
  }, []);

  return (
    <LayoutWithoutTitle location={location} hideNull>
      {loginLoading && <LoginLoading />}
      <FormContainer
        css={`
          padding-top: 40px;
          ${breakpoint(`mobile`)} {
            padding-top: 0;
          }
        `}
      >
        <PageTitle
          css={`
            margin: 0 0 48px 0;
            ${breakpoint(`mobile`)} {
              margin: 34px 0 24px 0;
            }
          `}
        >
          <h2>
            <Tit size="s1-5" color="sky" weight="normal">
              유니세프 한국위원회를 <br />
              방문해주셔서 감사합니다
            </Tit>
          </h2>
          <p
            css={`
              ${breakpoint(`mobile`)} {
                margin-top: 10px !important;
              }
            `}
          >
            로그인을 통해, 나의 후원정보를 확인해보세요.
          </p>
        </PageTitle>
        <form onSubmit={handleSubmit(onSubmit)}>
          <FormGroup>
            <Row>
              <InputText
                name="loginId"
                ref={register({
                  required: {
                    value: true,
                    message: `필수 입력입니다`,
                  },
                })}
                placeholder="아이디를 입력해 주세요."
              />
            </Row>
            {errors.loginId && (
              <ErrorMessage>{errors.loginId.message}</ErrorMessage>
            )}
            {` `}
            <Row
              css={`
                margin-top: 10px;
              `}
            >
              <InputPassword
                name="loginPw"
                ref={register({
                  required: {
                    value: true,
                    message: `필수 입력입니다`,
                  },
                })}
                placeholder="비밀번호를 입력해 주세요."
              />
            </Row>
            {errors.loginPw && (
              <ErrorMessage>{errors.loginPw.message}</ErrorMessage>
            )}
            <Row
              css={`
                margin-top: 12px;
              `}
            >
              <InputCheckbox
                label="아이디저장"
                name="isSavingId"
                ref={register}
              />
            </Row>
          </FormGroup>
          <Button type="submit" full>
            로그인
          </Button>
          <Row
            css={`
              justify-content: center;
              margin-top: 16px;
              a {
                display: inline-flex;
                align-items: center;
                color: #828385;
                &:last-child::after {
                  display: none;
                }
              }
              a::after {
                content: '';
                display: inline-block;
                width: 1px;
                height: 14px;
                background: #d4d4d4;
                margin: 0 10px;
              }
            `}
          >
            <Link to="/auth/find-id">아이디 찾기</Link>
            <Link to="/auth/find-pw">비밀번호 찾기</Link>
            <Link to="/auth/join">회원가입</Link>
          </Row>

          <FormGroup
            css={`
              margin: 40px 0 0;
              ${breakpoint(`mobile`)} {
                margin: 16px 0 0;
              }
            `}
          >
            <Row>
              <KakaoLogin
                clientId={process.env.KAKAO_JAVASCRIPT_KEY || ``}
                callbackUrl={`${process.env.SELF_URL}/auth/login`}
                isReturnProfile={true}
                role="login"
                render={(props) => (
                  <Button {...props} type="button" full sns="kakao">
                    카카오로 로그인
                  </Button>
                )}
                onSuccess={loginWithKakao}
                onFailure={(msg) => console.error(msg)}
              />
            </Row>
            <Row
              css={`
                margin-top: 14px;
                ${breakpoint(`mobile`)} {
                  margin-top: 10px;
                }
              `}
            >
              <NaverLogin
                clientId={process.env.NAVER_LOGIN_CLIENT_ID || ``}
                callbackUrl={`${process.env.SELF_URL}/auth/login`}
                isPopup={false}
                render={(props) => (
                  <Button type="button" sns="naver" full {...props}>
                    네이버로 로그인
                  </Button>
                )}
                onSuccess={loginWithNaver}
                onFailure={(e) => console.error(e)}
              />
            </Row>
          </FormGroup>
          <Row
            css={`
              margin: 40px 0 0;
              justify-content: center;
              gap: 40px;
              ${breakpoint(`mobile`)} {
                margin: 20px 0 0;
              }
            `}
          >
            <SocialButton onClick={() => googleLoginClick()}>
              <img src={iconGoogle} alt="구글" width="50" height="50" />
            </SocialButton>
            <FacebookLogin
              appId={process.env.FACEBOOK_LOGIN_APP_ID || ``}
              autoLoad={false}
              fields="name,email"
              disableMobileRedirect
              responseType="fbLogin"
              render={({ onClick }) => (
                <SocialButton onClick={onClick}>
                  <img
                    src={iconFacebook}
                    alt="페이스북"
                    width="50"
                    height="50"
                  />
                </SocialButton>
              )}
              dialogParams={{ response_type: 'fbcode' }}
              onSuccess={(response) => {
                loginWithFacebook(response);
              }}
              onFail={(error) => {
                console.log('Login Failed!', error);
              }}
              onProfileSuccess={(response) => {
                //console.log('Get Profile Success!', response);
              }}
            />
          </Row>
        </form>

        <LoginPageBanners>
          {banners.length > 0 && (
            <CampaignCard
              campaigns={banners}
              autoplay
              overflowHidden
              className="bn-slide"
              img
            />
          )}
        </LoginPageBanners>
      </FormContainer>
    </LayoutWithoutTitle>
  );
});

export default Login;
